/**
 * CloudSaveModal
 * Shows share-to-cloud options. On Android/iOS the native share sheet
 * opens and lets the user pick Google Drive, iCloud Drive, Dropbox, etc.
 * Falls back to a normal download if Web Share API is unavailable.
 */
import React, { useState } from 'react';
import autoBackupManager from '../utils/autoBackupManager';

const CloudSaveModal = ({ onClose }) => {
  const [step, setStep]     = useState('options');   // options | sharing | ready | done | error
  const [errorMsg, setErrorMsg] = useState('');
  const [withPhotos, setWithPhotos] = useState(true);
  const [preparedFile, setPreparedFile]     = useState(null);
  const [preparedBackup, setPreparedBackup] = useState(null);

  const canWebShare = 'share' in navigator;

  // Step 1 (async): build the backup file. We CANNOT share here — navigator.share()
  // must run inside a user gesture, and the await above would break it. So we
  // prepare the file, then let the user tap "Compartir ahora" (a fresh gesture).
  const prepare = async (includePhotos) => {
    setStep('sharing');
    try {
      const backup = includePhotos
        ? await autoBackupManager.createBackup()
        : await autoBackupManager.createBackupWithoutPhotos();
      const file = autoBackupManager.backupToShareFile(backup, includePhotos ? '' : '-sin-fotos');
      setPreparedBackup(backup);
      setPreparedFile(file);
      setStep('ready');
    } catch (e) {
      setErrorMsg(e.message || 'Error desconocido');
      setStep('error');
    }
  };

  // Step 2: called DIRECTLY from the button's onClick — fully synchronous up to
  // navigator.share(), so the user gesture is preserved.
  const doShare = () => {
    if (!preparedFile) return;
    if (navigator.canShare && navigator.canShare({ files: [preparedFile] })) {
      navigator.share({
        files: [preparedFile],
        title: 'Wayfare Backup',
        text: `Copia de seguridad Wayfare — ${new Date().toLocaleDateString('es-ES')}`,
      })
        .then(() => { autoBackupManager.markBackedUp(); setStep('done'); })
        .catch((e) => {
          if (e.name === 'AbortError') return; // user cancelled — stay on ready
          setErrorMsg(`${e.name || 'Error'}: ${e.message || 'desconocido'}`);
          setStep('error');
        });
    } else {
      setErrorMsg('Este navegador no permite compartir archivos.');
      setStep('error');
    }
  };

  const download = async (includePhotos) => {
    setStep('sharing');
    try {
      const backup = includePhotos
        ? await autoBackupManager.createBackup()
        : await autoBackupManager.createBackupWithoutPhotos();
      await autoBackupManager.downloadBackup(backup);
      setStep('done');
    } catch (e) {
      setErrorMsg(e.message || 'Error desconocido');
      setStep('error');
    }
  };

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={sheet}>

        <button onClick={onClose} style={closeBtn}>✕</button>

        {step === 'options' && (
          <>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>☁️</div>
            <h2 style={title}>Guardar en tu Nube</h2>
            <p style={subtitle}>
              Elige qué incluir y dónde guardarlo. Tus datos nunca pasan por nuestros servidores.
            </p>

            {/* Toggle con/sin fotos */}
            <div style={toggleRow}>
              <button
                onClick={() => setWithPhotos(true)}
                style={toggleBtn(withPhotos)}
              >
                📷 Con fotos
              </button>
              <button
                onClick={() => setWithPhotos(false)}
                style={toggleBtn(!withPhotos)}
              >
                📄 Sin fotos (más ligero)
              </button>
            </div>
            <p style={hint}>
              {withPhotos
                ? 'Backup completo. Puede ser grande si tienes muchas fotos.'
                : 'Solo texto: notas, listas, presupuestos, alojamientos. Mucho más rápido de compartir.'}
            </p>

            {/* Botones de acción */}
            {canWebShare && (
              <ActionBtn
                icon="📤"
                label="Compartir con..."
                sublabel={
                  /Android/i.test(navigator.userAgent)
                    ? 'Abre Google Drive, Dropbox, Gmail…'
                    : /iPhone|iPad/i.test(navigator.userAgent)
                    ? 'Abre iCloud Drive, Dropbox, Mail…'
                    : 'Abre el selector de apps'
                }
                color="#C4714A"
                onClick={() => prepare(withPhotos)}
              />
            )}

            <ActionBtn
              icon="📥"
              label="Descargar al dispositivo"
              sublabel="Guarda el archivo en tu carpeta de Descargas"
              color="#2D5A6E"
              onClick={() => download(withPhotos)}
            />

            {!canWebShare && (
              <p style={{ ...hint, color: '#e65100', marginTop: '1rem' }}>
                ⚠️ Tu navegador no soporta compartición directa. Usa "Descargar" y luego sube el archivo a Drive manualmente.
              </p>
            )}
          </>
        )}

        {step === 'sharing' && (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '1rem', animation: 'spin 1.2s linear infinite', display: 'inline-block' }}>⏳</div>
            <p style={{ color: '#555', fontSize: '1rem' }}>Preparando backup…</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {step === 'ready' && (
          <>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✅</div>
            <h2 style={title}>Backup listo</h2>
            <p style={subtitle}>
              Pulsa "Compartir ahora" y elige Google Drive, Dropbox, iCloud, Gmail… para guardarlo en tu nube.
              {preparedFile && ` (${(preparedFile.size / (1024 * 1024)).toFixed(1)} MB)`}
            </p>
            {preparedFile && preparedFile.size > 25 * 1024 * 1024 && (
              <p style={{ ...hint, color: '#e65100' }}>
                ⚠️ Es un archivo grande; si compartir falla, usa "Descargar" y súbelo a tu nube a mano.
              </p>
            )}
            <ActionBtn
              icon="📤"
              label="Compartir ahora"
              sublabel="Abre el selector de apps de tu dispositivo"
              color="#C4714A"
              onClick={doShare}
            />
            <ActionBtn
              icon="📥"
              label="Descargar al dispositivo"
              sublabel="Si prefieres subirlo manualmente"
              color="#2D5A6E"
              onClick={() => { if (preparedBackup) autoBackupManager.downloadBackup(preparedBackup).then(() => setStep('done')).catch(() => {}); }}
            />
            <button onClick={() => setStep('options')} style={retryBtn}>← Volver</button>
          </>
        )}

        {step === 'done' && (
          <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: '0.75rem' }}>✅</div>
            <h3 style={{ color: '#2D5A6E', marginBottom: '0.5rem' }}>Listo</h3>
            <p style={{ color: '#555', fontSize: '0.95rem', lineHeight: '1.6' }}>
              Tu backup está guardado. Recuerda hacerlo cada semana o después de añadir contenido importante.
            </p>
            <button onClick={onClose} style={doneBtn}>Cerrar</button>
          </div>
        )}

        {step === 'error' && (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>⚠️</div>
            <h3 style={{ color: '#c62828', marginBottom: '0.5rem' }}>Error al compartir</h3>
            <p style={{ color: '#555', fontSize: '0.85rem', marginBottom: '1.25rem', lineHeight: '1.6' }}>
              {errorMsg}
              <br /><br />
              Prueba a descargar el archivo al dispositivo y súbelo manualmente a Drive o Dropbox.
            </p>
            <ActionBtn
              icon="📥"
              label="Descargar al dispositivo"
              sublabel=""
              color="#2D5A6E"
              onClick={() => download(withPhotos)}
            />
            <button onClick={() => setStep('options')} style={retryBtn}>← Volver</button>
          </div>
        )}

      </div>
    </div>
  );
};

// ── Sub-components ──────────────────────────────────────────────────────────

const ActionBtn = ({ icon, label, sublabel, color, onClick }) => (
  <button onClick={onClick} style={actionBtn(color)}>
    <span style={{ fontSize: '1.4rem' }}>{icon}</span>
    <span style={{ textAlign: 'left' }}>
      <span style={{ display: 'block', fontWeight: '700', fontSize: '1rem' }}>{label}</span>
      {sublabel && <span style={{ display: 'block', fontSize: '0.78rem', opacity: 0.8, marginTop: '0.1rem' }}>{sublabel}</span>}
    </span>
  </button>
);

// ── Styles ──────────────────────────────────────────────────────────────────

const overlay = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.55)',
  zIndex: 10000,
  display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
};

const sheet = {
  background: 'white',
  borderRadius: '24px 24px 0 0',
  padding: '2rem 1.5rem 2.5rem',
  width: '100%',
  maxWidth: '480px',
  maxHeight: '90vh',
  overflowY: 'auto',
  position: 'relative',
  boxShadow: '0 -4px 30px rgba(0,0,0,0.2)',
};

const closeBtn = {
  position: 'absolute', top: '1rem', right: '1rem',
  background: '#f0f0f0', border: 'none', borderRadius: '50%',
  width: '2rem', height: '2rem', cursor: 'pointer',
  fontSize: '0.9rem', fontWeight: '700', color: '#555',
};

const title = {
  color: '#1a1a2e', margin: '0 0 0.5rem', fontSize: '1.5rem', fontWeight: '900',
};

const subtitle = {
  color: '#666', fontSize: '0.9rem', lineHeight: '1.5', margin: '0 0 1.25rem',
};

const toggleRow = {
  display: 'flex', gap: '0.5rem', marginBottom: '0.5rem',
};

const toggleBtn = (active) => ({
  flex: 1,
  padding: '0.6rem 0.5rem',
  borderRadius: '10px',
  border: active ? '2px solid #C4714A' : '2px solid #ddd',
  background: active ? '#F2DDD2' : '#f9f9f9',
  color: active ? '#C4714A' : '#666',
  fontWeight: '600',
  fontSize: '0.85rem',
  cursor: 'pointer',
  transition: 'all .15s',
});

const hint = {
  color: '#888', fontSize: '0.8rem', marginBottom: '1.25rem', lineHeight: '1.5',
};

const actionBtn = (color) => ({
  display: 'flex', alignItems: 'center', gap: '1rem',
  width: '100%', padding: '1rem 1.25rem',
  background: color, color: 'white',
  border: 'none', borderRadius: '14px',
  cursor: 'pointer', marginBottom: '0.75rem',
  boxShadow: `0 3px 10px ${color}55`,
  transition: 'opacity .15s',
});

const doneBtn = {
  marginTop: '1.25rem',
  background: '#2D5A6E', color: 'white',
  border: 'none', borderRadius: '12px',
  padding: '0.85rem 2.5rem',
  fontSize: '1rem', fontWeight: '700', cursor: 'pointer',
};

const retryBtn = {
  display: 'block', width: '100%', marginTop: '0.75rem',
  background: 'none', border: '1px solid #ccc',
  borderRadius: '10px', padding: '0.75rem',
  color: '#666', fontSize: '0.9rem', cursor: 'pointer',
};

export default CloudSaveModal;
